import * as Cesium from 'cesium';
import {
  captureCameraPose,
  flyToSettled,
  type CameraPose,
  type SettleCamera,
  type SettleViewer,
} from './lib/cameraFlight';

/**
 * Camera-overlay flight controller (Phase V, click-to-fly).
 *
 * The "demo moment": clicking a camera marker flies the Cesium camera smoothly
 * down to that location at low altitude, then a video overlay fades in. On
 * exit we fly back to wherever the camera was before the dive — so we capture
 * the prior pose at the moment we fly in and restore it on flyBack.
 *
 * Capture + flights ride the shared settle grammar (lib/cameraFlight.ts,
 * #252): poses are captured world-fixed (positionWC, transform-proof) and a
 * starved flight is LANDED at its destination instead of abandoned mid-air —
 * the same fix the flythrough/AAR restore idiom inherited.
 */

/** Altitude (metres) the camera dives to over the selected camera. At ~250 m
 *  the camera's coverage cone fills much of the view, giving directional
 *  context underneath the overlay. */
export const OVERLAY_ALTITUDE_M = 250;
/** flyTo duration (seconds) for both the dive-in and the fly-back-out. */
export const FLY_DURATION_S = 2.0;
/** Altitude (metres) the camera flies to on a click-fire incident zoom (R.3.1).
 *  At ~30 km the visible viewport is roughly a 20 km radius at Cesium's default
 *  FOV — enough context to see the incident plus the nearest response resources,
 *  without diving underground the way the 250 m camera overlay does. */
export const INCIDENT_ALTITUDE_M = 30_000;

/**
 * Country-zoom extent framing all of Canada (#138). Used as the no-state default
 * first-paint view (replacing the former GTA-tight default) and as the fallback
 * home view when an operator hasn't set one (#149). Framed as a Cesium Rectangle
 * so the camera height auto-fits the bounds. Spans Pacific (BC, ~-141°) to
 * Atlantic (NL, ~-52.6°) and the southern border (~41.7°N) to the high Arctic
 * (Ellesmere I., ~83.1°N).
 */
export const CANADA_VIEW_BOUNDS = {
  west: -141.0,
  south: 41.7,
  east: -52.6,
  north: 83.1,
} as const;

/** A saved camera vantage: a look-straight-down point + its altitude (#149). */
export interface CameraView {
  lat: number;
  lon: number;
  heightM: number;
}

/**
 * "National view" camera action (Phase 6) — fly to the all-Canada frame,
 * UNCONDITIONALLY. The deliberate counterpart to the Home action (H), which
 * prefers the operator's saved vantage and only falls back to Canada when none
 * is set. National takes ONLY the controller and never reads a saved home, so
 * "the whole country, every time" is a STRUCTURAL guarantee — a saved vantage
 * cannot leak in. Mirrors the #392 wall-reset recenter flight body; manual only
 * (no auto-snap). No-op until the controller is ready. Pinned by
 * nationalView.test.ts.
 */
export function flyNationalView(ctrl: Pick<FlyController, 'flyToBounds'> | null): void {
  if (!ctrl) return;
  const { west, south, east, north } = CANADA_VIEW_BOUNDS;
  void ctrl.flyToBounds(west, south, east, north);
}

export interface FlyController {
  /** Capture the current pose, then fly down to (lat, lon) at OVERLAY_ALTITUDE_M.
   *  Resolves when the flight completes (so the caller can fade the overlay in). */
  flyToCamera(lat: number, lon: number): Promise<void>;
  /** Fly back to the pose captured by the most recent flyToCamera. No-op (resolves
   *  immediately) if nothing was captured yet. */
  flyBack(): Promise<void>;
  /** Click-fire incident zoom (R.3.1): fly straight down to (lat, lon) at
   *  INCIDENT_ALTITUDE_M for a ~20 km incident viewport. Does NOT capture a prior
   *  pose — incident zoom is navigation, not an overlay dive, so there's no
   *  flyBack. Resolves when the flight completes (or its wall-clock fallback). */
  flyToIncident(lat: number, lon: number): Promise<void>;
  /** Search "fly to place" (#139): frame a geocoded result's extent — the
   *  Google-familiar zoom. Flies to the [west,south,east,north] bbox as a Cesium
   *  Rectangle, so the viewport matches the place's real footprint (a city fills
   *  more screen than a lake). Navigation, so no prior-pose capture. */
  flyToBounds(west: number, south: number, east: number, north: number): Promise<void>;
  /** Read the current camera vantage (lat/lon/height) — backs "Set as my home
   *  view" (#149), capturing wherever the operator has framed. */
  getView(): CameraView;
  /** Fly straight down to a saved vantage at its stored altitude (#149 home). */
  flyToView(view: CameraView): Promise<void>;
}

/** Minimal slice of the Cesium viewer this controller drives — keeps the unit
 *  test free of a full WebGL viewer. */
interface FlyableViewer extends SettleViewer {
  camera: SettleCamera;
}

export function createFlyController(
  viewer: FlyableViewer,
  opts: { altitudeM?: number; durationS?: number; incidentAltitudeM?: number } = {},
): FlyController {
  const altitudeM = opts.altitudeM ?? OVERLAY_ALTITUDE_M;
  const durationS = opts.durationS ?? FLY_DURATION_S;
  const incidentAltitudeM = opts.incidentAltitudeM ?? INCIDENT_ALTITUDE_M;
  let priorPose: CameraPose | null = null;

  // Shared settle grammar (#252): resolve on real complete/cancel; a starved
  // flight (throttled tab, headless browser) is LANDED at its destination by
  // the wall-clock backstop — the overlay always appears over the right view
  // and flyBack always ends exactly on the captured pose.
  const fly = (options: {
    destination: unknown;
    orientation?: { heading: number; pitch: number; roll: number };
  }): Promise<void> => flyToSettled(viewer, options, durationS).then(() => undefined);

  return {
    flyToCamera(lat, lon) {
      priorPose = captureCameraPose(viewer.camera);
      return fly({
        destination: Cesium.Cartesian3.fromDegrees(lon, lat, altitudeM),
      });
    },
    flyBack() {
      if (!priorPose) return Promise.resolve();
      const pose = priorPose;
      return fly({
        destination: pose.destination,
        orientation: { heading: pose.heading, pitch: pose.pitch, roll: pose.roll },
      });
    },
    flyToIncident(lat, lon) {
      return fly({
        destination: Cesium.Cartesian3.fromDegrees(lon, lat, incidentAltitudeM),
      });
    },
    flyToBounds(west, south, east, north) {
      return fly({
        destination: Cesium.Rectangle.fromDegrees(west, south, east, north),
      });
    },
    getView() {
      // positionWC, not position: world-fixed even under a camera transform —
      // a saved home/region view must never persist frame-local coordinates.
      const carto = Cesium.Cartographic.fromCartesian(viewer.camera.positionWC.clone());
      return {
        lat: Cesium.Math.toDegrees(carto.latitude),
        lon: Cesium.Math.toDegrees(carto.longitude),
        heightM: carto.height,
      };
    },
    flyToView(view) {
      return fly({
        destination: Cesium.Cartesian3.fromDegrees(view.lon, view.lat, view.heightM),
      });
    },
  };
}
